import { useCallback, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	buildAtlAttributionHeaders,
	extractTraceInfoFromResponse,
	isIntentDetectionSuccess,
	jiraIntentDetectionQuery,
	KnowledgeDiscoveryJiraIntentDetectionModel,
	KnowledgeDiscoveryJiraSearchExperience,
	normaliseTraceInfo,
} from '@atlassian/search-client';

import { getSessionIdFromLocalStorage, sha256Hash } from '../../../../common/utils/events/utils';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	remapVitalStatsEventForQuickFind,
	TASK_NAMES,
} from '../../../../common/utils/events/vital-stats-events';
import { serveJiraNINFpsNLQNavigation } from '../../../../common/utils/experiments/experiment-exports';
import { IntentMode } from '../../../../common/utils/intent-detection';
import { onIntentDetectionResponse } from '../../../../common/utils/quick-find/events/intent-detection-response';
import { useQuickFindAnalytics } from '../../../../controllers/quick-find/analytics';
import { useIntentDetectionVitalStatsTaskState } from '../../../../controllers/quick-find/intent-detection-vitalstats-context';
import {
	useAnalyticsAttributes,
	useCloudId,
	useOrgId,
	usePrimaryProduct,
	useSearchSessionId,
} from '../../../../controllers/store/bootstrap';

import { modelIdValidator } from './utils';

/**
 * Real intent detection via @atlassian/search-client.
 */
export const useIntentDetectionService: (options?: {
	defaultMode?: IntentMode;
}) => (query: string) => Promise<IntentMode> = ({
	defaultMode = IntentMode.GNIN,
}: { defaultMode?: IntentMode } = {}) => {
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const sessionId = getSessionIdFromLocalStorage();
	const taskStateRef = useIntentDetectionVitalStatsTaskState();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const intentDetectionModelId = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
	})?.modelId;
	const [analyticsAttributes] = useAnalyticsAttributes();

	// Memoize atl-attribution headers to avoid recreating on every render
	const atlAttributionHeaders = useMemo(
		() =>
			buildAtlAttributionHeaders({
				cloudId,
				activationId: analyticsAttributes?.activationId,
				product: primaryProduct,
			}),
		[cloudId, analyticsAttributes?.activationId, primaryProduct],
	);

	const fireAnalytics = useCallback(
		(action: 'success' | 'fail', attributes: Record<string, any> = {}) => {
			fireAnalyticsEvent(
				onIntentDetectionResponse({ action, attributes: { ...attributes, searchSessionId } }),
			);
		},
		[searchSessionId, fireAnalyticsEvent],
	);

	return useCallback(
		async (query: string): Promise<IntentMode> => {
			// create siteARI based on primary product
			const siteId = `ari:cloud:${primaryProduct}::site/${cloudId}`;
			// VitalStats: Generate task ID and record start time
			const taskId = crypto.randomUUID();
			const uxTaskId = taskStateRef.current.id;
			const startTime = performance.now();
			// Query Analytics attributes
			const queryHash = sha256Hash(query);
			const queryLength = query.length;

			fireAnalyticsEvent(
				remapVitalStatsEventForQuickFind(
					onTaskStart({
						searchSessionId: searchSessionId || '',
						taskId,
						task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
						moreContext: {
							uxTaskId,
							queryHash,
							queryLength,
						},
					}),
				),
			);

			try {
				const response = await jiraIntentDetectionQuery(
					{
						query,
						siteId,
						sessionId,
						orgId: orgId ?? '',
						experience: KnowledgeDiscoveryJiraSearchExperience.QUICK_FIND,
						model: modelIdValidator(intentDetectionModelId)
							? intentDetectionModelId
							: KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC,
					},
					atlAttributionHeaders,
				);

				const result = response.data?.knowledgeDiscovery.jiraIntentDetection;
				const latencyMs = performance.now() - startTime;
				const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
					? normaliseTraceInfo(extractTraceInfoFromResponse(response))
					: {};

				if (!isIntentDetectionSuccess(result)) {
					const errorName = 'UnsuccessfulResponse';
					const errorMessage = 'Query typename mismatch';

					fireAnalytics('fail', { queryHash, errorName, errorMessage, queryLength });
					fireAnalyticsEvent(
						remapVitalStatsEventForQuickFind(
							onTaskFail({
								searchSessionId: searchSessionId || '',
								taskId,
								task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
								moreContext: {
									uxTaskId,
									queryHash,
									queryLength,
									latencyMs,
									errorName,
									errorMessage,
									...traceInfo,
								},
							}),
						),
					);

					return defaultMode;
				}

				const intentMode = result.searchClassification?.type || defaultMode;

				fireAnalytics('success', { queryHash, queryLength });
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskSuccess({
							searchSessionId: searchSessionId || '',
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
							moreContext: {
								uxTaskId,
								queryHash,
								queryLength,
								latencyMs,
								queryClassification: intentMode,
								...traceInfo,
							},
						}),
					),
				);

				return intentMode;
			} catch (error) {
				const latencyMs = performance.now() - startTime;
				const errorName = (error as Error)?.name || 'UnknownError';
				const errorMessage = (error as Error)?.message ?? 'An unknown error occurred';

				fireAnalytics('fail', { queryHash, errorName, errorMessage, queryLength });
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskFail({
							searchSessionId: searchSessionId || '',
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
							moreContext: {
								uxTaskId,
								queryHash,
								queryLength,
								latencyMs,
								errorName: (error as Error)?.name,
								errorMessage: (error as Error)?.message,
								...(fg('rovo-search-vitalstats-tracing-headers')
									? normaliseTraceInfo(undefined)
									: {}),
							},
						}),
					),
				);

				return defaultMode;
			}
		},
		[
			cloudId,
			orgId,
			primaryProduct,
			defaultMode,
			fireAnalytics,
			searchSessionId,
			sessionId,
			fireAnalyticsEvent,
			intentDetectionModelId,
			taskStateRef,
			atlAttributionHeaders,
		],
	);
};
